import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import {
  createSparkEngine,
  DEFAULT_SPARK_OPTIONS,
  type SparkEngine,
  type SparkOptions,
} from '@/src/renderer/lib/clickSpark'
import { getSoundsEnabled, onSoundsChanged } from '@/src/renderer/lib/sounds/soundSetting'
import './ClickSpark.scss'

export interface ClickSparkApi {
  /** Sprüht an einer Stelle im Fenster (Client-Koordinaten). */
  burst: (x: number, y: number, options?: Partial<SparkOptions>) => void
  /** Sprüht aus der Mitte des Elements — geht damit auch per Tastatur. Farbe aus dessen `--space-700`. */
  burstAt: (element: Element | null, options?: Partial<SparkOptions>) => void
}

const noop: ClickSparkApi = { burst: () => {}, burstAt: () => {} }

const ClickSparkContext = createContext<ClickSparkApi>(noop)

/** Ohne Provider (Tests, einzelne Stories) tut die API nichts. */
export function useClickSpark(): ClickSparkApi {
  return useContext(ClickSparkContext)
}

// Lifespace-Farben hängen an Bereichen, nicht an `:root` — deshalb am Element lesen.
function readColor(element: Element | null): string {
  const style = getComputedStyle(element ?? document.documentElement)
  return (
    style.getPropertyValue('--space-700').trim() ||
    style.getPropertyValue('--brand').trim() ||
    'currentColor'
  )
}

// Canvas versteht kein `var(--token)` — Tokens hier auflösen, damit Aufrufer keine Hexwerte brauchen.
function resolveColor(color: string, element: Element | null): string {
  const token = /^var\((--[\w-]+)\)$/.exec(color.trim())?.[1]
  if (!token) return color
  return getComputedStyle(element ?? document.documentElement).getPropertyValue(token).trim() || readColor(element)
}

/**
 * Ein Canvas-Overlay pro Fenster für den Click Spark. Hängt am Ton-Schalter:
 * Wer die Klänge ausschaltet, will auch kein Feuerwerk.
 *
 * @example
 * const { burstAt } = useClickSpark()
 * burstAt(buttonRef.current)
 * burstAt(buttonRef.current, { count: 12, radius: 24, color: 'var(--success)' })
 */
export const ClickSparkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<SparkEngine | null>(null)
  const enabledRef = useRef(true)

  useEffect(() => {
    if (!canvasRef.current) return
    const engine = createSparkEngine(canvasRef.current)
    engineRef.current = engine
    return () => {
      engine.destroy()
      engineRef.current = null
    }
  }, [])

  useEffect(() => {
    let changedMeanwhile = false
    const off = onSoundsChanged(enabled => {
      changedMeanwhile = true
      enabledRef.current = enabled
    })
    void getSoundsEnabled().then(enabled => {
      if (!changedMeanwhile) enabledRef.current = enabled
    })
    return off
  }, [])

  const api = useMemo<ClickSparkApi>(() => {
    const fire = (x: number, y: number, element: Element | null, options?: Partial<SparkOptions>) => {
      if (!enabledRef.current || !engineRef.current) return
      engineRef.current.burst(x, y, {
        ...DEFAULT_SPARK_OPTIONS,
        ...options,
        color: options?.color ? resolveColor(options.color, element) : readColor(element),
      })
    }
    return {
      burst: (x, y, options) => fire(x, y, null, options),
      burstAt(element, options) {
        if (!element) return
        const rect = element.getBoundingClientRect()
        // Inaktive Tabs liegen in `display: none` — dort gibt es nichts zu feiern.
        if (rect.width === 0 && rect.height === 0) return
        fire(rect.left + rect.width / 2, rect.top + rect.height / 2, element, options)
      },
    }
  }, [])

  return (
    <ClickSparkContext.Provider value={api}>
      {children}
      <canvas ref={canvasRef} className="click-spark" aria-hidden="true" />
    </ClickSparkContext.Provider>
  )
}
